<template>
	<b-modal
		id="new-animal-modal"
		:ok-disabled="!valid"
		title="New animal"
		@ok="ok"
	>
		<p class="text-justify">First, it should be checked whether the animal is already in the database. You can search either by chip ID (for dogs), or by name, owner name, animal type and breed. Clicking on the <strong>OK</strong> button will perform the search. If the animal exists, you will be informed, otherwise the new animal will be created and displayed for further editing.</p>

		<div>
			<b-tabs
				content-class="mt-3"
				v-model="tabIndex"
			>
				<b-tab
					title="General search"
					active
				>
					<FieldEditor
						:record="record"
						:field="fields.find(f => f.field === 'animalName')"
						@change="onChange"
					/>
					<FieldEditor
						:record="record"
						:field="fields.find(f => f.field === 'ownerName')"
						@change="onChange"
					/>
					<FieldEditor
						:record="record"
						:field="fields.find(f => f.field === 'animalType')"
						@change="onChange"
					/>
					<FieldEditor
						:record="record"
						:field="fields.find(f => f.field === 'animalBreed')"
						v-if="record.animalType && showBreed"
						@change="onChange"
					/>
				</b-tab>
				<b-tab title="Search by chip ID">
					<FieldEditor
						:record="record"
						:field="fields.find(f => f.field === 'animalChipID')"
						@change="onChange"
					/>
				</b-tab>
			</b-tabs>
		</div>

		<div v-if="result">
			<hr>
			<div class="alert alert-info">
				<span v-if="result.created">
					Animal has been created, redirecting...
				</span>
				<span v-else-if="result.id">
					Animal already exists,
					<strong>
						<router-link :to="'/animal/' + result.id">click here to open</router-link>
					</strong>
				</span>
				<span v-else>Something went wrong.</span>
			</div>
		</div>
	</b-modal>
</template>

<script>
import apiClient from '../apiClient'
import FieldEditor from '@/components/FieldEditor'
import { ANIMAL_FIELDS_FLAT } from '@/fields/animal_fields'

export default {
	components: {
		FieldEditor,
	},
	data() {
		return {
			fields: ANIMAL_FIELDS_FLAT.filter((f) =>
				[
					'animalBreed',
					'animalChipID',
					'animalName',
					'animalType',
					'ownerName',
				].includes(f.field)
			),
			record: {
				animalBreed: null,
				animalChipID: null,
				animalName: null,
				animalType: null,
				ownerName: null,
			},
			result: null,
			showBreed: false,
			tabIndex: 0,
		}
	},
	computed: {
		valid() {
			const {
				animalBreed,
				animalChipID,
				animalName,
				animalType,
				ownerName,
			} = this.record

			let valid = false
			if (this.tabIndex === 0) {
				// general mode
				valid =
					animalBreed != null &&
					animalName != null &&
					animalType != null &&
					ownerName != null
			} else {
				// chip ID mode
				valid = animalChipID
			}

			return valid && this.result == null // don't request again
		},
	},
	watch: {
		tabIndex() {
			this.result = null
		}
	},
	methods: {
		async ok(e) {
			if (!this.valid) return
			this.result = null // don't request again
			e.preventDefault() // don't close modal

			const {
				animalBreed,
				animalChipID,
				animalName,
				animalType,
				ownerName,
			} = this.record

			let body = {}
			if (this.tabIndex === 0) {
				// general mode
				body = {
					animalBreed: animalBreed._id || animalBreed, // editor currently returns breed object instead of just the ID
					animalName,
					animalType,
					ownerName,
				}
			} else {
				// chip ID mode
				body = { animalChipID }
			}

			this.result = await apiClient.animalGetId(body)
			if (this.result.created) {
				this.$router.push('/animal/' + this.result.id)
			}
		},
		onChange(args) {
			this.result = null
			const { value, field } = args
			this.record[field] = value
			if (field === 'animalType') {
				this.record.animalBreed = null
				// reinitializing breed list:
				this.showBreed = false
				this.$nextTick(() => {
					this.showBreed = true
				})
			}
		},
	},
}
</script>
